import { useUpdateProduct } from '@/features/menu/hooks/useMenu';
import { useT } from '@/i18n';
import { ImageUploadError, uploadImage } from '@/lib/image-upload';
import { mediaSrc } from '@/lib/media-url';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { loc } from '@/utils/localized';
import type { Product } from '@restaurant/api-contracts';
import { BlockTitle, Preloader } from 'framework7-react';
import { useRef, useState } from 'react';

const MAX_EDGE = 1600;

function photoSrc(stored: string): string {
  return /^https?:\/\//i.test(stored) ? stored : mediaSrc(stored, 'small');
}

export default function ProductPhotoBlock({ product }: { product: Product }) {
  const t = useT();
  const pf = t.menu.productForm;
  const updateProduct = useUpdateProduct();
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  const inputRef = useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stored = loc(product.imageUrl);
  const url = preview ?? (stored ? photoSrc(stored) : null);

  function errorMessage(err: unknown): string {
    if (err instanceof ImageUploadError) {
      if (err.code === 'tooLarge') return pf.photoTooLarge;
      if (err.code === 'wrongType') return pf.photoWrongType;
    }
    return pf.photoUploadFailed;
  }

  function save(body: { imageUrl: { en: string } | null }): Promise<void> {
    return new Promise((resolve, reject) => {
      updateProduct.mutate(
        { productId: product.id, body },
        { onSuccess: () => resolve(), onError: reject },
      );
    });
  }

  async function handlePick(file: File) {
    if (!restaurantId) return;
    setError(null);
    const objUrl = URL.createObjectURL(file);
    setPreview(objUrl);
    setBusy(true);
    try {
      const { key } = await uploadImage(file, restaurantId, 'product', { maxEdge: MAX_EDGE });
      await save({ imageUrl: { en: key } });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
      setPreview(null);
      URL.revokeObjectURL(objUrl);
    }
  }

  async function handleRemove() {
    setError(null);
    setBusy(true);
    try {
      await save({ imageUrl: null });
    } catch {
      setError(pf.photoUploadFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <BlockTitle>{pf.photo}</BlockTitle>
      <div style={{ padding: '0 var(--f7-block-padding-horizontal, 16px)' }}>
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: 240,
            height: 160,
            borderRadius: 'var(--f7-list-inset-border-radius, 12px)',
            overflow: 'hidden',
            border: '1px solid var(--f7-list-item-border-color, rgba(0,0,0,0.12))',
            background: 'var(--f7-list-bg-color, var(--f7-page-bg-color))',
          }}
        >
          <button
            type="button"
            disabled={busy}
            aria-label={url ? pf.replacePhoto : pf.addPhoto}
            onClick={() => inputRef.current?.click()}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              padding: 0,
              border: 'none',
              cursor: busy ? 'default' : 'pointer',
              background: 'transparent',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              color: 'var(--f7-text-color-secondary, #8e8e93)',
            }}
          >
            {url ? (
              <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : busy ? (
              <Preloader />
            ) : (
              <>
                <i className="f7-icons" style={{ fontSize: 34 }}>
                  photo_on_rectangle
                </i>
                <span style={{ fontSize: 14 }}>{pf.addPhoto}</span>
                <span style={{ fontSize: 12 }}>PNG · JPG · WebP</span>
              </>
            )}
            {url && busy && (
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.35)',
                }}
              >
                <Preloader color="white" />
              </span>
            )}
          </button>
          {url && !busy && (
            <button
              type="button"
              aria-label={pf.removePhoto}
              onClick={handleRemove}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: 'none',
                cursor: 'pointer',
                background: 'rgba(0,0,0,0.55)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="f7-icons" style={{ fontSize: 18 }}>
                xmark
              </i>
            </button>
          )}
        </div>
        {error && (
          <p
            style={{ margin: '8px 0 0', color: 'var(--f7-theme-color-red, #ff3b30)', fontSize: 13 }}
          >
            {error}
          </p>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void handlePick(file);
          }}
        />
      </div>
    </>
  );
}
